import axios from 'axios';

// Rate limiting helper
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Simple request queue to prevent rate limiting
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1000; // 1 second between Overpass requests

const throttledOverpassRequest = async (query) => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  
  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    await delay(MIN_REQUEST_INTERVAL - timeSinceLastRequest);
  }
  
  lastRequestTime = Date.now();
  
  return axios.post(
    'https://overpass-api.de/api/interpreter',
    query,
    { 
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      timeout: 30000
    }
  );
};

/**
 * Geocode an address or POI using Nominatim (OSM)
 */
export const geocodeAddress = async (query) => {
  try {
    const response = await axios.get(
      `https://nominatim.openstreetmap.org/search`,
      {
        params: {
          q: query,
          format: 'json',
          limit: 5,
          addressdetails: 1,
        },
        headers: {
          'User-Agent': 'HamBestSpots/1.0'
        },
        timeout: 10000
      }
    );

    if (response.data && response.data.length > 0) {
      return response.data.map(result => ({
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        display_name: result.display_name,
        type: result.type,
        class: result.class
      }));
    }

    return [];
  } catch (error) {
    console.error('Geocoding error:', error);
    return [];
  }
};

/**
 * Fetch elevation data for multiple points in batch
 */
export const fetchElevationBatch = async (points) => {
  try {
    const batchSize = 100;
    const batches = [];
    
    for (let i = 0; i < points.length; i += batchSize) {
      batches.push(points.slice(i, i + batchSize));
    }
    
    const allElevations = [];
    
    for (const batch of batches) {
      const locations = batch.map(p => `${p.lat},${p.lng}`).join('|');
      
      try {
        const response = await axios.get(
          `https://api.open-elevation.com/api/v1/lookup?locations=${locations}`,
          { timeout: 15000 }
        );
        
        if (response.data && response.data.results) {
          allElevations.push(...response.data.results.map(r => r.elevation || 0));
        } else {
          allElevations.push(...new Array(batch.length).fill(0));
        }
      } catch (error) {
        console.error('Error fetching elevation batch:', error);
        allElevations.push(...new Array(batch.length).fill(0));
      }
    }
    
    return allElevations;
  } catch (error) {
    console.error('Error in fetchElevationBatch:', error);
    return new Array(points.length).fill(0);
  }
};

/**
 * Fetch single point elevation
 */
export const fetchElevation = async (lat, lng) => {
  try {
    const response = await axios.get(
      `https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lng}`,
      { timeout: 10000 }
    );
    
    if (response.data && response.data.results && response.data.results[0]) {
      return response.data.results[0].elevation || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error fetching elevation:', error);
    return 0;
  }
};

/**
 * Fetch OSM peaks/summits (Type: Sommet)
 */
export const fetchOSMPeaks = async (lat, lng, radiusMeters) => {
  try {
    const query = `
      [out:json][timeout:25];
      (
        node["natural"="peak"](around:${radiusMeters},${lat},${lng});
        node["natural"="hill"](around:${radiusMeters},${lat},${lng});
      );
      out body;
    `;

    const response = await throttledOverpassRequest(query);

    if (response.data && response.data.elements) {
      return response.data.elements.map(e => ({
        lat: e.lat,
        lng: e.lon,
        name: e.tags?.name || 'Sommet sans nom',
        elevation: e.tags?.ele ? parseInt(e.tags.ele) : null,
        type: 'summit',
        osmId: e.id
      }));
    }

    return [];
  } catch (error) {
    console.error('Error fetching OSM peaks:', error);
    return [];
  }
};

/**
 * Fetch OSM picnic sites (Type: Confort)
 */
export const fetchOSMPicnicSites = async (lat, lng, radiusMeters) => {
  try {
    const query = `
      [out:json][timeout:25];
      (
        node["tourism"="picnic_site"](around:${radiusMeters},${lat},${lng});
        way["tourism"="picnic_site"](around:${radiusMeters},${lat},${lng});
        node["leisure"="picnic_table"](around:${radiusMeters},${lat},${lng});
      );
      out center body;
    `;

    const response = await throttledOverpassRequest(query);

    if (response.data && response.data.elements) {
      return response.data.elements.map(e => ({
        lat: e.center ? e.center.lat : e.lat,
        lng: e.center ? e.center.lon : e.lon,
        name: e.tags?.name || 'Aire de pique-nique',
        type: 'comfort',
        osmId: e.id,
        hasShelter: e.tags?.shelter === 'yes' || e.tags?.amenity === 'shelter',
        hasToilets: false,
        hasWater: false,
      }));
    }

    return [];
  } catch (error) {
    console.error('Error fetching picnic sites:', error);
    return [];
  }
};

/**
 * Fetch QRM data for a summit point
 */
export const fetchQRMData = async (lat, lng, radiusMeters = 3000) => {
  try {
    const query = `
      [out:json][timeout:10];
      (
        node["man_made"="mast"]["tower:type"~"communication|broadcast"](around:${radiusMeters},${lat},${lng});
        way["landuse"="residential"](around:${radiusMeters},${lat},${lng});
        way["power"="line"]["voltage"~"[0-9]{5,}"](around:${radiusMeters},${lat},${lng});
        way["landuse"="industrial"](around:${radiusMeters},${lat},${lng});
      );
      out body;
    `;

    const response = await axios.post(
      'https://overpass-api.de/api/interpreter',
      query,
      { 
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 12000
      }
    );

    if (response.data && response.data.elements) {
      const elements = response.data.elements;
      
      return {
        hasBroadcastTransmitters: elements.some(e => 
          e.tags && (e.tags['tower:type'] === 'communication' || e.tags['tower:type'] === 'broadcast')
        ),
        isUrban: elements.some(e => 
          e.tags && (e.tags.landuse === 'residential' || e.tags.landuse === 'industrial')
        ),
        hasHighVoltageLine: elements.some(e => 
          e.tags && e.tags.power === 'line' && e.tags.voltage && parseInt(e.tags.voltage) >= 100000
        ),
      };
    }

    return {};
  } catch (error) {
    console.error('Error fetching QRM data:', error);
    return {};
  }
};

/**
 * Fetch amenities near a comfort point
 */
export const fetchComfortAmenities = async (lat, lng, radiusMeters = 500) => {
  try {
    const query = `
      [out:json][timeout:10];
      (
        way["highway"](around:${radiusMeters},${lat},${lng});
        node["amenity"="parking"](around:${radiusMeters},${lat},${lng});
        way["amenity"="parking"](around:${radiusMeters},${lat},${lng});
        node["tourism"="viewpoint"](around:${radiusMeters},${lat},${lng});
        node["amenity"="shelter"](around:${radiusMeters},${lat},${lng});
        node["amenity"="toilets"](around:${radiusMeters},${lat},${lng});
        node["amenity"="drinking_water"](around:${radiusMeters},${lat},${lng});
        node["amenity"="bbq"](around:${radiusMeters},${lat},${lng});
        way["highway"="path"](around:${radiusMeters},${lat},${lng});
        way["highway"="footway"](around:${radiusMeters},${lat},${lng});
      );
      out body;
    `;

    const response = await axios.post(
      'https://overpass-api.de/api/interpreter',
      query,
      { 
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 12000
      }
    );

    if (response.data && response.data.elements) {
      const elements = response.data.elements;
      
      return {
        hasRoadAccess: elements.some(e => e.tags?.highway && !['path', 'footway', 'cycleway'].includes(e.tags.highway)),
        hasParking: elements.some(e => e.tags?.amenity === 'parking'),
        hasViewpoint: elements.some(e => e.tags?.tourism === 'viewpoint'),
        hasShelter: elements.some(e => e.tags?.amenity === 'shelter'),
        hasToilets: elements.some(e => e.tags?.amenity === 'toilets'),
        hasWater: elements.some(e => e.tags?.amenity === 'drinking_water'),
        hasBbq: elements.some(e => e.tags?.amenity === 'bbq'),
        hasTrail: elements.some(e => e.tags?.highway === 'path' || e.tags?.highway === 'footway'),
      };
    }

    return {};
  } catch (error) {
    console.error('Error fetching comfort amenities:', error);
    return {};
  }
};

/**
 * Search for OSM POIs by name
 */
export const searchOSMPOI = async (query, lat, lng, radiusMeters = 50000) => {
  try {
    const osmQuery = `
      [out:json][timeout:15];
      (
        node["name"~"${query}",i](around:${radiusMeters},${lat},${lng});
        way["name"~"${query}",i](around:${radiusMeters},${lat},${lng});
      );
      out center body 10;
    `;

    const response = await axios.post(
      'https://overpass-api.de/api/interpreter',
      osmQuery,
      { 
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        timeout: 18000
      }
    );

    if (response.data && response.data.elements) {
      return response.data.elements.map(e => ({
        lat: e.center ? e.center.lat : e.lat,
        lng: e.center ? e.center.lon : e.lon,
        name: e.tags?.name || 'POI',
        type: e.tags?.natural || e.tags?.tourism || e.tags?.amenity || 'poi'
      }));
    }

    return [];
  } catch (error) {
    console.error('Error searching OSM POI:', error);
    return [];
  }
};
